import { memo } from 'react'
import { Menu } from '@highlight/ui'
import { Brain } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useNextMessageModelTakesReasoningEffort } from '../../../../hooks/use-next-message-model'
import {
  useReasoningControlsEnabled,
  useReasoningEffortSetting,
  type ReasoningEffortSetting,
} from '../../../../hooks/use-reasoning-effort'

interface ReasoningEffortOption {
  value: ReasoningEffortSetting
  label: string
  description: string
}

const REASONING_EFFORT_OPTIONS: Array<ReasoningEffortOption> = [
  { value: 'auto', label: 'Auto', description: 'The model decides how long to think' },
  { value: 'low', label: 'Low', description: 'Quicker answers' },
  { value: 'medium', label: 'Medium', description: 'Balanced speed and depth' },
  { value: 'high', label: 'High', description: 'Thinks longest, uses more credits' },
]

/**
 * Picks how hard the next message's model thinks before answering. Hidden
 * when that model ignores the setting. The trigger names the level only when it
 * isn't Auto, so the default composer stays compact.
 */
export const ReasoningEffortMenu = memo(({ chatId, className }: { chatId: string | undefined; className?: string }) => {
  const enabled = useReasoningControlsEnabled()
  const modelTakesEffort = useNextMessageModelTakesReasoningEffort(chatId)
  const [setting, setSetting] = useReasoningEffortSetting()

  if (!enabled || !modelTakesEffort) {
    return null
  }

  const active = REASONING_EFFORT_OPTIONS.find((option) => option.value === setting) ?? REASONING_EFFORT_OPTIONS[0]

  return (
    <Menu>
      <Menu.Trigger
        variant="ghost"
        size="lg"
        aria-label={`Reasoning: ${active.label}`}
        className={twMerge(
          'text-foreground-secondary data-[popup-open]:bg-element-hover gap-1 px-2 [&_svg]:!size-4',
          active.value === 'auto' && 'w-8 px-0',
          className,
        )}
      >
        <Brain aria-hidden="true" />
        {active.value !== 'auto' && <span className="text-xs font-medium">{active.label}</span>}
      </Menu.Trigger>
      <Menu.Content side="top" align="end" className="w-64">
        <Menu.Group>
          <Menu.GroupLabel>Reasoning</Menu.GroupLabel>
          <Menu.RadioGroup value={active.value} onValueChange={(value) => setSetting(value as ReasoningEffortSetting)}>
            {REASONING_EFFORT_OPTIONS.map((option) => (
              <Menu.RadioItem key={option.value} value={option.value}>
                <Menu.ItemText description={option.description}>{option.label}</Menu.ItemText>
                <Menu.RadioItemIndicator />
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </Menu.Group>
      </Menu.Content>
    </Menu>
  )
})
ReasoningEffortMenu.displayName = 'ReasoningEffortMenu'
